// Whose name a reply signs with (2026-10-30,
// docs/decisions/2026-10-30-restored-work-signature-owner.md).
//
// A reply box holds the draft with [AGENT_FIRST_NAME] already resolved to the
// CSR's own first name (resolveAgentNamePlaceholders() in text-format.js). Work
// saved from that box and restored for another CSR carried the first CSR's
// name. These helpers run that rule in reverse (the saved work holds the
// token, never a name), move a resolved name from one CSR to another, and spot
// a footer that names someone other than the person sending.
//
// signatureInfo: { signature, signatureIncludesAgentName } (the server's
// companies.js entry, or the UI's companySignatureInfo()). A company whose
// signature never carries a name (AM) has nothing to reverse or check in its
// footer.

import { resolveAgentNamePlaceholders } from './text-format.js'

export const AGENT_NAME_TOKEN = '[AGENT_FIRST_NAME]'
// The send warning's reason code (logged with ids only, never a name).
export const SIGNATURE_NAME_MISMATCH = 'signature_name_mismatch'

const escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// "Sam" from "Sam Smith" (the signed-in identity's display name), '' for none.
export function firstNameOf(name) {
  return String(name ?? '').trim().split(/\s+/)[0] ?? ''
}

// Text split into its pieces: tags and line breaks stay as they are, the text
// between them is matched as a whole (a line of a plain box, or the text of
// an HTML paragraph).
const PIECES = /(<[^>]*>|\r?\n)/
const isSeparator = (piece) => /^(<[^>]*>|\r?\n)$/.test(piece)

// The pieces of the token's own source text that hold the token outside the
// footer, as [resolvedTrimmed, tokenTrimmed] pairs for this first name.
function loneTokenPieces(sources, footerToken, firstName) {
  const out = []
  for (const src of sources ?? []) {
    if (typeof src !== 'string' || !src.includes(AGENT_NAME_TOKEN)) continue
    for (const piece of src.split(PIECES)) {
      if (isSeparator(piece) || !piece.includes(AGENT_NAME_TOKEN) || piece.includes(footerToken)) continue
      const token = piece.trim()
      const resolved = token.replaceAll(AGENT_NAME_TOKEN, firstName).trim()
      if (resolved && resolved !== token) out.push([resolved, token])
    }
  }
  return out
}

// The reverse of resolveAgentNamePlaceholders() for one CSR's first name: the
// footer "<first name> | <signature>" becomes "[AGENT_FIRST_NAME] |
// <signature>" again, and a piece that held the token on its own in a source
// text (the suggestion's draft, a step's message, the signature template;
// "Thanks, [AGENT_FIRST_NAME]") becomes the token again where the text still
// holds it resolved. Anything the CSR typed stays exactly as typed.
export function tokenizeAgentName(text, signatureInfo, firstName, { sources = [] } = {}) {
  if (typeof text !== 'string' || !text || !firstName) return text
  const signature = signatureInfo?.signature ?? ''
  const footerToken = `${AGENT_NAME_TOKEN} | ${signature}`
  let out = text
  if (signature && signatureInfo?.signatureIncludesAgentName !== false) {
    out = out.replaceAll(`${firstName} | ${signature}`, footerToken)
  }
  const lone = loneTokenPieces(sources, footerToken, firstName)
  if (!lone.length) return out
  return out.split(PIECES).map((piece) => {
    if (isSeparator(piece)) return piece
    const hit = lone.find(([resolved]) => piece.trim() === resolved)
    if (!hit) return piece
    const lead = piece.match(/^\s*/)[0]
    const trail = piece.match(/\s*$/)[0]
    return `${lead}${hit[1]}${trail}`
  }).join('')
}

// One CSR's resolved name moved to another's: tokenized for `fromFirstName`,
// then resolved for `toFirstName` (by `load`, the box's own load path, or the
// shared resolve rule).
export function retargetAgentName(text, signatureInfo, fromFirstName, toFirstName, { sources = [], load = null } = {}) {
  if (typeof text !== 'string' || !text) return text
  const tokenized = tokenizeAgentName(text, signatureInfo, fromFirstName, { sources })
  return load ? load(tokenized) : resolveAgentNamePlaceholders(tokenized, signatureInfo, toFirstName)
}

// The reply as lines of plain text (HTML paragraphs and breaks as lines).
function plainLines(text) {
  return String(text ?? '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .split(/\r?\n/)
}

// The name on the reply's footer line, "<name> | <signature>" (the last one),
// or null when the reply has no such line. '' when the line has no name.
export function footerAgentName(text, signatureInfo) {
  const signature = signatureInfo?.signature
  if (!text || !signature) return null
  // The name is short (a first name, maybe two words); text typed after the
  // signature on the same line ("Sam | 1A Auto test") still counts.
  const re = new RegExp(`^\\s*([^|]{0,40}?)\\s*\\|\\s*${escapeRe(signature)}(?:\\s.*)?$`)
  let name = null
  for (const line of plainLines(text)) {
    const m = line.match(re)
    if (m) name = m[1].trim()
  }
  return name
}

// The same first name, allowing a short form ("Matt" for "Matthew").
function sameFirstName(a, b) {
  const x = String(a ?? '').trim().toLowerCase()
  const y = String(b ?? '').trim().toLowerCase()
  if (!x || !y) return false
  return x === y || x.startsWith(y) || y.startsWith(x)
}

// The send safety net: the reply's footer names someone other than the
// person sending. { name, expected } or null. Never for a company whose
// signature carries no name (AM), a footer still holding the token, a footer
// with no name, or a sender with no first name.
export function signatureNameMismatch(text, signatureInfo, senderFirstName) {
  if (!text || !senderFirstName || signatureInfo?.signatureIncludesAgentName === false) return null
  const name = footerAgentName(text, signatureInfo)
  if (!name || name.includes(AGENT_NAME_TOKEN)) return null
  if (sameFirstName(name, senderFirstName)) return null
  return { name, expected: senderFirstName }
}

// The one-click fix: the footer's name becomes the sender's first name.
export function fixSignatureName(text, signatureInfo, senderFirstName) {
  const name = footerAgentName(text, signatureInfo)
  if (name == null || !senderFirstName) return text
  const re = new RegExp(`${name ? escapeRe(name) : ''}[ \\t]*\\|[ \\t]*${escapeRe(signatureInfo.signature)}`, 'g')
  let last = null
  for (const m of String(text).matchAll(re)) last = m
  if (!last) return text
  return `${text.slice(0, last.index)}${senderFirstName} | ${signatureInfo.signature}${text.slice(last.index + last[0].length)}`
}

// The warning the box shows before send.
export function signatureMismatchText({ name, expected }) {
  return `The signature says ${name}, but you're signed in as ${expected}. Fix the signature?`
}
